import { Pill, Text } from '@innovationdepartment/proxima-ui';
import type { MouseEvent } from 'react';
import PauseResumeButton from '../../components/PauseResumeButton';
import {
  ActionsCell,
  Card,
  ChipColumn,
  ClippedLine,
  DestinationChip,
  ErrorLine,
  LoadWarning,
  NameCell,
  NameLink,
  RowMain,
  StatusDot,
  TimestampCell
} from '../../components/shared.styles';
import { PAUSE_AVAILABLE } from '../../featureFlags';
import { type AudienceRowModel, statusHoverText } from '../../lib/buildAudienceRows';
import { platformLabel, summarizeDeliveries } from '../../lib/deliverySummary';
import { audienceStatusPillVariant } from '../../lib/deriveAudienceStatus';
import { formatTimestampLines, formatUserId } from '../../lib/formatters';

type AudienceListRowProps = {
  row: AudienceRowModel;
  updating: boolean;
  onOpen: () => void;
  onSetPaused: (paused: boolean) => void;
};

/**
 * One definition in the list.
 *
 * The name is the link to the detail page; clicking anywhere else on the row opens it too, for the mouse.
 */
const AudienceListRow = ({ row, updating, onOpen, onSetPaused }: AudienceListRowProps) => {
  const { definition, latestSnapshot, destinations, derived } = row;
  const lastRun = latestSnapshot?.completedAt ?? latestSnapshot?.startedAt ?? null;
  const deliveries = summarizeDeliveries(destinations);

  const onRowClick = (event: MouseEvent<HTMLDivElement>) => {
    const { target } = event;
    /* The link navigates itself; actions and their popups never do */
    if (target instanceof Element && target.closest('a, [data-no-row-toggle]')) return;
    onOpen();
  };

  return (
    <Card failed={derived.status === 'Failed'}>
      {/* Keyboard and assistive tech reach the page through the name link below */}
      <RowMain data-audience-row={definition.id} onClick={onRowClick}>
        <NameCell>
          {/* A native title keeps the prompt on hover without a popper opening on focus */}
          <NameLink
            to={String(definition.id)}
            aria-label={`Open ${definition.name}`}
            title={definition.nlPrompt ?? 'Built from the filter controls'}
          >
            <Text variant="body1Semibold" ellipsis>
              {definition.name}
            </Text>
          </NameLink>
          {definition.description ? (
            <Text variant="body2" color="neutral60" ellipsis>
              {definition.description}
            </Text>
          ) : null}
          {derived.errorMessage ? <ErrorLine>{derived.errorMessage}</ErrorLine> : null}
          {row.loadError ? (
            <LoadWarning>Some details didn't load: {row.loadError}</LoadWarning>
          ) : null}
        </NameCell>
        {/* An Unknown or stale status names the failed lookup on hover */}
        <div title={statusHoverText(row)}>
          <Pill label={derived.status} variant={audienceStatusPillVariant(derived.status)} />
        </div>
        <ChipColumn>
          {row.destinationsUnavailable ? (
            <Text variant="body2" color="neutral60">
              —
            </Text>
          ) : null}
          {deliveries.length === 0 && !row.destinationsUnavailable ? (
            <Text variant="body2" color="neutral60">
              Not delivered
            </Text>
          ) : (
            deliveries.map(summary => (
              <DestinationChip
                key={summary.platform}
                title={`Worst status: ${summary.worstStatus}`}
              >
                <StatusDot status={summary.worstStatus} />
                {platformLabel(summary.platform)}
                <small>
                  {summary.liveCount} {summary.liveCount === 1 ? 'destination' : 'destinations'}
                </small>
              </DestinationChip>
            ))
          )}
        </ChipColumn>
        <TimestampCell>
          <TimestampLines value={definition.createdAt} />
          <Text variant="body3" color="neutral60">
            <ClippedLine title={definition.createdByUserId ?? undefined}>
              {formatUserId(definition.createdByUserId)}
            </ClippedLine>
          </Text>
        </TimestampCell>
        <TimestampCell>
          <TimestampLines value={lastRun} />
        </TimestampCell>
        {/* The confirm popup renders in a portal, but React bubbles its clicks through this cell */}
        <ActionsCell
          data-no-row-toggle
          onClick={event => event.stopPropagation()}
          onKeyDown={event => event.stopPropagation()}
        >
          {PAUSE_AVAILABLE && definition.status !== 'deleted' ? (
            <PauseResumeButton
              audienceName={definition.name}
              paused={definition.status === 'paused'}
              disabled={updating}
              onConfirm={() => onSetPaused(definition.status !== 'paused')}
            />
          ) : null}
        </ActionsCell>
      </RowMain>
    </Card>
  );
};

/** A timestamp as a date line over a time line, or a dash when unset. */
const TimestampLines = ({ value }: { value: string | null }) => {
  const lines = formatTimestampLines(value);
  if (!lines) return <Text variant="body2">—</Text>;
  return (
    <>
      <Text variant="body2">{lines.date}</Text>
      <Text variant="body2">{lines.time}</Text>
    </>
  );
};

export default AudienceListRow;
